import React, { useEffect } from "react"
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom"
import { observer } from "mobx-react-lite"
import { useStores } from "~models"
import TabLayout from "./TabLayout"
import { LegacyScreenHost, NavigationBridge } from "./navigation"
import { appScreens } from "~navigators/components/screens.config"

import AppIntroScreen from "~pages/auth-stack/app-intro"
import LoginScreen from "~pages/auth-stack/login"
import RegisterScreen from "~pages/auth-stack/register"
import ForgetPasswordScreen from "~pages/auth-stack/forget-password"
import VerifyCodeScreen from "~pages/auth-stack/verify-code"
import ResetPasswordScreen from "~pages/auth-stack/reset-password"

import ChatListScreen from "~pages/app-stack/home-stack/chat-list"
import ContactListScreen from "~pages/app-stack/home-stack/contact-list"
import GroupChatListScreen from "~pages/app-stack/home-stack/group-chat-list"
import MeScreen from "~pages/app-stack/home-stack/me"
import ChatScreen from "~pages/app-stack/chat"
import { ConsoleScreen } from "~screens/console/console-screen"
import { ROUTE_PATHS } from "./route-map"
import ScreenLayout from "./ScreenLayout"
import { ConfiguredLegacyScreen } from "./legacy-screen-route"
import { PopupProvider } from "~components/Popup"
import { WebviewProvider } from "~components/WebviewContext"

const GuestGate = observer(function GuestGate({ children }: { children: React.ReactNode }) {
  const { authStore } = useStores()
  if (authStore.hasToken) return <Navigate to="/chats" replace />
  return <>{children}</>
})

const AuthGate = observer(function AuthGate({ children }: { children: React.ReactNode }) {
  const { authStore } = useStores()
  if (!authStore.hasToken) return <Navigate to="/login" replace />
  return <>{children}</>
})

const AppRoutesInner = observer(function AppRoutesInner() {
  const { authStore, websocketStore, walletStore, notificationStore } = useStores()

  useEffect(() => {
    if (!authStore.hasToken) {
      websocketStore.close()
      return undefined
    }
    websocketStore.connect()
    walletStore.getWalletInfo()
    notificationStore.refreshUserUnreadNotificationCount()
    return undefined
  }, [authStore.hasToken, websocketStore, walletStore, notificationStore])

  return (
    <Routes>
      {/* 认证页 */}
      <Route
        element={
          <GuestGate>
            <ScreenLayout headerShown={false} />
          </GuestGate>
        }
      >
        <Route index element={<AppIntroScreen />} />
        <Route path="login" element={<LoginScreen />} />
        <Route path="register" element={<RegisterScreen />} />
        <Route path="forgot-password" element={<ForgetPasswordScreen />} />
        <Route path="verify-code" element={<LegacyScreenHost component={VerifyCodeScreen} />} />
        <Route path="reset-password" element={<LegacyScreenHost component={ResetPasswordScreen} />} />
      </Route>

      {/* 主应用 */}
      <Route
        element={
          <AuthGate>
            <ScreenLayout headerShown={false} />
          </AuthGate>
        }
      >
        <Route element={<TabLayout />}>
          <Route path="chats" element={<ChatListScreen />} />
          <Route path="contacts" element={<ContactListScreen />} />
          <Route path="groups" element={<GroupChatListScreen />} />
          <Route path="me" element={<MeScreen />} />
          {/* {__DEV__ ? <Route path="debug" element={<ConsoleScreen />} /> : null} */}
        </Route>

        <Route path="room" element={<LegacyScreenHost component={ChatScreen} />} />
        <Route path="user" element={<Navigate to="/chats" replace />} />

        {appScreens.map(({ name, component: Component, options }) => {
          const path = (ROUTE_PATHS[name] || `/${name}`).replace(/^\//, "")
          return (
            <Route
              key={name}
              path={path}
              element={
                <ConfiguredLegacyScreen name={name} component={Component} options={options} />
              }
            />
          )
        })}
      </Route>

      <Route path="*" element={<Navigate to={authStore.hasToken ? "/chats" : "/login"} replace />} />
    </Routes>
  )
})

export default function AppRouter({ children }: { children?: React.ReactNode }) {
  return (
    <BrowserRouter future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
      <NavigationBridge />
      <PopupProvider>
        <WebviewProvider>
          <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, height: "100%" }}>
            <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
              <AppRoutesInner />
            </div>
            {children}
          </div>
        </WebviewProvider>
      </PopupProvider>
    </BrowserRouter>
  )
}
